import { useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSessionTimeoutContext } from '../context/SessionTimeoutContext';

interface UseSessionTimeoutProps {
  onLogout: () => void;
}

export const useSessionTimeout = ({ onLogout }: UseSessionTimeoutProps) => {
  const { config } = useSessionTimeoutContext();
  const logoutInProgress = useRef(false);
  const absoluteTimeoutRef = useRef<number | null>(null);

  // Idle timeout handler
  const onIdle = () => {
    if (logoutInProgress.current) {
      return;
    }

    handleTimeout('IDLE_TIMEOUT_EXPIRED');
  };

  // Initialize idle timer
  const { reset, getRemainingTime, start, pause } = useIdleTimer({
    timeout: config.idleTimeoutMs,
    onIdle,
    debounce: 500,
    eventsThrottle: 500,
    startOnMount: true,
    startManually: false,
  });

  // Handle timeout (either idle or absolute)
  const handleTimeout = (reason: 'IDLE_TIMEOUT_EXPIRED' | 'ABSOLUTE_TIMEOUT_EXPIRED') => {
    if (logoutInProgress.current) {
      return;
    }

    logoutInProgress.current = true;
    
    
    // Notify other tabs
    try {
      sessionStorage.setItem('session_timeout_logout', JSON.stringify({
        timestamp: Date.now(),
        reason,
      }));
    } catch (error) {
      console.error('[SessionTimeout] Failed to notify other tabs:', error);
    }

    // Call the logout function
    onLogout();
  };

  // Setup absolute timeout (8 hours maximum session)
  useEffect(() => {

    absoluteTimeoutRef.current = setTimeout(() => {
      handleTimeout('ABSOLUTE_TIMEOUT_EXPIRED');
    }, config.absoluteTimeoutMs);

    return () => {
      if (absoluteTimeoutRef.current) {
        clearTimeout(absoluteTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.absoluteTimeoutMs]);

  // Debug logging - log remaining time every 10 seconds
//   useEffect(() => {
//     const debugInterval = setInterval(() => {
//     //   const remaining = getRemainingTime();
//     //   const remainingMinutes = Math.floor(remaining / 1000 / 60);
//     //   const remainingSeconds = Math.floor((remaining / 1000) % 60);
//     //   const sessionDuration = Date.now() - sessionStartTime.current;
//     //   const sessionMinutes = Math.floor(sessionDuration / 1000 / 60);
      
//     }, 10000); // Every 10 seconds

//     return () => clearInterval(debugInterval);
//   }, [getRemainingTime]);

  // Listen for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'session_timeout_logout' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          
          if (!logoutInProgress.current) {
            handleTimeout(data.reason || 'IDLE_TIMEOUT_EXPIRED');
          }
        } catch (error) {
          console.error('[SessionTimeout] Failed to parse logout event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    reset,
    getRemainingTime,
    start,
    pause,
  };
};
